import { useCallback, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OtpForm } from "@/components/auth/otp-form";
import { PasswordForm } from "@/components/auth/password-form";
import { useAuth, type UseAuthReturn } from "@/hooks/use-auth";
import type { AuthUser } from "@/providers/auth-provider";
import { apiUrl } from "@/lib/api";

export function LoginPage(): ReactNode {
  const { login }: UseAuthReturn = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const handleOtpLogin = useCallback(
    async (_phone: string, _otp: string) => {
      // OTP not connected yet — show message
      setError("כניסה עם וואטסאפ עדיין לא פעילה. השתמש בסיסמא.");
    },
    [],
  );

  const handlePasswordLogin = useCallback(
    async (phone: string, password: string) => {
      setError(null);
      try {
        const res = await fetch(apiUrl("/api/auth/platform/login"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ phone, password }),
        });
        const json = await res.json() as {
          success: boolean;
          error?: string;
          data?: { accessToken: string; user: AuthUser };
        };
        if (!json.success || !json.data) {
          setError(json.error ?? "שגיאת התחברות");
          return;
        }
        login(json.data.accessToken, json.data.user);
        navigate("/");
      } catch {
        setError("שגיאת תקשורת — נסה שוב");
      }
    },
    [login, navigate],
  );

  return (
    <div className="flex min-h-dvh items-center justify-center p-4">
      <Card className="w-full max-w-[400px]">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {"\u05DB\u05E0\u05D9\u05E1\u05D4 \u05DC\u05E0\u05D9\u05D4\u05D5\u05DC"}
          </CardTitle>
          <CardDescription>
            {"\u05D4\u05EA\u05D7\u05D1\u05E8 \u05DC\u05E4\u05E0\u05DC \u05D4\u05E0\u05D9\u05D4\u05D5\u05DC \u05E9\u05DC \u05D4\u05E2\u05E1\u05E7"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-lg bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive text-center">
              {error}
            </div>
          )}
          <Tabs defaultValue="password" dir="rtl">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="otp">
                {"\u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4"}
              </TabsTrigger>
              <TabsTrigger value="password">
                {"\u05E1\u05D9\u05E1\u05DE\u05D0"}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="otp" className="mt-4">
              <OtpForm onSubmit={handleOtpLogin} />
            </TabsContent>
            <TabsContent value="password" className="mt-4">
              <PasswordForm onSubmit={handlePasswordLogin} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
